import React, { useState, useRef, useContext } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  Pressable,
  StyleSheet,
  Platform,
  ActivityIndicator,
  FlatList,
  type StyleProp,
  type ViewStyle,
} from 'react-native';
import FontAwesome6 from '@react-native-vector-icons/fontawesome6/static';
import { useAppSelector, useAppDispatch } from '../../../redux/helper';
import { fs, s, iconSize, v, borderWidth } from '../../../utils/libs';
import { isEmployeeAdminRole } from '../../../utils/roles';
import { GTFonts, PoppinsFonts, theme } from '../../../theme';
import { BottomSheetModal } from '@gorhom/bottom-sheet';
import LocationBottomSheet from '../../LocationPicker/LocationBottomSheet';
import ErrorContext from '../../../Context/ErrorContext';
import { useNavigation, useRoute } from '@react-navigation/native';
import { isAllLocationsEligibleRoute } from '../../../hooks/useAllLocationsRoute';
import {
  setIsAllLocations,
  setLocationSwitching,
  setCurrentLocationId,
  setCurrentLocation,
  incrementLocationVersion,
} from '../../../redux/locationSlice';
import useLocation from '../../../hooks/useLocation';

type FontAwesome6SolidIconName = Extract<
  React.ComponentProps<typeof FontAwesome6>,
  { iconStyle: 'solid' }
>['name'];

// Backoffice (owner) slices for clearing state
import {
  clearCompleted as boClearCompleted,
  clearPending as boClearPending,
  clearPartial as boClearPartial,
  clearTableSearch as boClearTableSearch,
} from '../../../redux/backoffice/ordersSlice';
import { clearSummary } from '../../../redux/backoffice/summarySlice';
import {
  clearTableSearch as boProductsClearTableSearch,
  setProducts as boSetProducts,
} from '../../../redux/backoffice/productsSlice';
import {
  setCurrentAndLastMonth,
  setDailyPayments,
  setRevenueOverTime,
  setWeeklySalesData,
  setProductsReport,
} from '../../../redux/backoffice/reportsSlice';
import { clearReport } from '../../../redux/backoffice/reportsTableSlice';
import { clearInvoices } from '../../../redux/backoffice/billingSlice';
import { setEmployees } from '../../../redux/backoffice/employeesSlice';
import {
  setMonthlySales,
  setSalesOverTime,
  setCategorySales,
  setTransactions,
} from '../../../redux/backoffice/salesSlice';
import { setDashboardData } from '../../../redux/backoffice/dashboardSlice';
import {
  setMonthlyData,
  setAnnualPoints,
  setCategoryItems,
  setSummaryData,
  resetExpensesList,
  setDetailData,
} from '../../../redux/backoffice/expensesSlice';
import {
  setTargetAmount,
  setTargetError,
  setTargetLoading,
} from '../../../redux/backoffice/targetsSlice';

interface BackOfficeHeaderLocationPickerProps {
  textColor?: string;
  containerStyle?: StyleProp<ViewStyle>;
}

const BackOfficeHeaderLocationPicker: React.FC<
  BackOfficeHeaderLocationPickerProps
> = ({ textColor = '#fff', containerStyle }) => {
  const navigation = useNavigation<any>();
  const dispatch = useAppDispatch();
  const route = useRoute();
  const { handleError } = useContext(ErrorContext);
  const bottomSheetRef = useRef<BottomSheetModal | null>(null);
  const [modalVisible, setModalVisible] = useState(false);

  const currentLocation = useAppSelector(s => s.location.currentLocation);
  const currentLocationId = useAppSelector(s => s.location.currentLocationId);
  const locations = useAppSelector(s => s.location.locations);
  const isAllLocations = useAppSelector(s => s.location.isAllLocations);
  const isLocationSwitching = useAppSelector(
    s => s.location.isLocationSwitching,
  );
  const employeedata = useAppSelector(
    state => state.auth.inApp.account.employeeInfo,
  );

  const { switchLocation, isAdmin } = useLocation();

  // Check if current route supports "All Locations" option
  const showAllLocationsOption = isAllLocationsEligibleRoute(
    route.name,
    locations,
  );

  // Don't render if user is not admin
  if (!isAdmin) {
    return null;
  }

  const clearOwnerState = () => {
    try {
      // Backoffice orders
      dispatch(boClearCompleted());
      dispatch(boClearPending());
      dispatch(boClearPartial());
      dispatch(boClearTableSearch());

      // Backoffice products
      dispatch(boProductsClearTableSearch());
      dispatch(boSetProducts({ items: [], total: 0, page: 1, pageSize: 500 }));

      // Backoffice summary
      dispatch(clearSummary());

      // Backoffice reports
      dispatch(setDailyPayments([]));
      dispatch(setRevenueOverTime(null));
      dispatch(setWeeklySalesData([] as any));
      dispatch(
        setCurrentAndLastMonth({
          lastMonth: { totalSales: 0 },
          currentMonth: { totalSales: 0 },
        }),
      );
      dispatch(
        setProductsReport({
          docs: [],
          totalDocs: 0,
          totalPages: 0,
          page: 1,
          perPage: 0,
          metadata: {
            totalItemsSold: 0,
            totalAmountWasted: 0,
            totalItemsWasted: 0,
            totalUpdatedProducts: 0,
            totalUpdatedProductsAmount: 0,
            totalSalesMade: 0,
          },
        } as any),
      );

      // Backoffice reports table
      dispatch(clearReport());

      // Backoffice billing
      dispatch(clearInvoices());

      // Backoffice employees
      dispatch(setEmployees({ items: [], total: 0, page: 1, pageSize: 100 }));

      // Backoffice sales
      dispatch(setMonthlySales([]));
      dispatch(setSalesOverTime(null));
      dispatch(setCategorySales([]));
      dispatch(
        setTransactions({
          items: [],
          total: 0,
          page: 1,
          pageSize: 100,
          totalPages: 0,
          totalAmount: 0,
        }),
      );

      // Backoffice dashboard
      dispatch(
        setDashboardData({
          totalSales: 0,
          totalQuantitiesSold: 0,
          totalMassSold: 0,
          totalProfit: 0,
          totalOrders: 0,
          totalExpenses: 0,
        }),
      );

      // Backoffice expenses
      dispatch(setMonthlyData({ payments: 0, stocks: 0, others: 0 }));
      dispatch(setAnnualPoints([]));
      dispatch(setCategoryItems([]));
      dispatch(setSummaryData(null));
      dispatch(resetExpensesList());
      dispatch(setDetailData(null));

      // Backoffice targets
      dispatch(setTargetLoading(false));
      dispatch(setTargetError(null));
      dispatch(setTargetAmount(null));
    } catch (e) {
      console.warn('clearOwnerState error', e);
    }
  };

  const handleSelectAllLocations = async () => {
    setModalVisible(false);
    bottomSheetRef.current?.dismiss();

    // If already in all locations mode, do nothing
    if (isAllLocations) {
      return;
    }

    dispatch(setLocationSwitching(true));

    try {
      // Clear state first - this clears all cached data
      clearOwnerState();

      // Wait for React to process the clear actions
      await new Promise(resolve => setTimeout(resolve, 150));

      // Now set the flag (this also increments locationVersion which triggers refetches)
      dispatch(setIsAllLocations(true));

      // Wait for screens to start their fetch operations
      await new Promise(resolve => setTimeout(resolve, 300));
    } finally {
      dispatch(setLocationSwitching(false));
    }
  };

  const handleSelectLocation = async (locationId: string) => {
    setModalVisible(false);
    bottomSheetRef.current?.dismiss();

    // Check if trying to switch to the same location (and not in all locations mode)
    if (!isAllLocations && currentLocationId === locationId) {
      return;
    }

    // Check permissions
    if (!isEmployeeAdminRole(employeedata?.bp_employee_role)) {
      handleError(
        'You do not have permission to switch locations.',
        'error',
        true,
      );
      return;
    }

    dispatch(setLocationSwitching(true));

    try {
      // Turn off all locations mode if it was on
      if (isAllLocations) {
        dispatch(setIsAllLocations(false));
      }

      // Switch location via API
      await switchLocation(locationId);

      // Clear cached backoffice data
      clearOwnerState();

      // Wait for React to process
      await new Promise(resolve => setTimeout(resolve, 150));

      // Trigger refetches
      dispatch(incrementLocationVersion());

      // Wait for screens to start fetching
      await new Promise(resolve => setTimeout(resolve, 300));
    } catch (error) {
      console.error('Location switch error:', error);
      handleError(
        'Failed to switch location. Please try again.',
        'error',
        true,
      );
    } finally {
      dispatch(setLocationSwitching(false));
    }
  };

  const getLocationIcon = (
    locationType?: string,
  ): FontAwesome6SolidIconName => {
    const type = locationType?.toLowerCase() || '';
    switch (type) {
      case 'outlet':
      case 'shop':
        return 'store';
      case 'warehouse':
        return 'warehouse';
      case 'office':
        return 'building';
      default:
        return 'location-dot';
    }
  };

  const displayText = isAllLocations
    ? 'All Locations'
    : currentLocation?.bp_location_name || 'Select Location';

  const truncatedDisplayText =
    displayText.length > 25 ? displayText.slice(0, 25) + '...' : displayText;

  const renderLocationItem = ({ item }: { item: any }) => {
    const isCurrentLocation =
      !isAllLocations &&
      currentLocationId &&
      (item.bp_location_id === currentLocationId ||
        item._id === currentLocationId);

    return (
      <Pressable
        onPress={() => handleSelectLocation(item.bp_location_id || item._id)}
        style={({ pressed, hovered }: any) => [
          styles.locationItem,
          Platform.OS === 'web' && styles.locationItemWeb,
          Platform.OS === 'web' && hovered && styles.locationItemHovered,
          pressed && { opacity: 0.85 },
        ]}>
        <View
          style={[
            styles.locationItemContent,
            isCurrentLocation && styles.currentLocationItem,
          ]}>
          <FontAwesome6
            name={getLocationIcon(item.bp_location_type)}
            iconStyle="solid"
            size={iconSize(20)}
            color={isCurrentLocation ? theme.colors.successDark : '#1560bd'}
            style={styles.locationIcon}
          />
          <View style={styles.locationInfo}>
            <Text style={styles.locationName}>{item.bp_location_name}</Text>
            <Text style={styles.locationDetails}>
              {item.bp_location_code} • {item.bp_location_type}
            </Text>
          </View>
          <View style={styles.badgeContainer}>
            {item.bp_location_is_default && (
              <View style={styles.defaultBadge}>
                <Text style={styles.defaultBadgeText}>Default</Text>
              </View>
            )}
            {isCurrentLocation && (
              <FontAwesome6
                name="circle-check"
                iconStyle="solid"
                size={iconSize(20)}
                color="#22c55e"
                style={styles.currentLocationIcon}
              />
            )}
          </View>
        </View>
      </Pressable>
    );
  };

  return (
    <View style={[styles.container, containerStyle]}>
      <TouchableOpacity
        style={styles.selector}
        onPress={() => {
          setModalVisible(true);
          requestAnimationFrame(() => bottomSheetRef.current?.present?.());
        }}
        disabled={isLocationSwitching}>
        {isAllLocations && (
          <FontAwesome6
            name="layer-group"
            iconStyle="solid"
            size={iconSize(14)}
            color={textColor}
            style={styles.allLocationsIcon}
          />
        )}
        <Text
          style={[styles.selectorText, { color: textColor }]}
          numberOfLines={1}>
          {truncatedDisplayText}
        </Text>
        {isLocationSwitching ? (
          <ActivityIndicator size="small" color={textColor} />
        ) : (
          <FontAwesome6
            name="chevron-down"
            iconStyle="solid"
            size={iconSize(12)}
            color={textColor}
          />
        )}
      </TouchableOpacity>

      <LocationBottomSheet
        bottomSheetRef={bottomSheetRef}
        title="Select Location"
        onClose={() => setModalVisible(false)}
        footer={
          isEmployeeAdminRole(employeedata?.bp_employee_role) ? (
            <View style={styles.footerContainer}>
              <TouchableOpacity
                onPress={() => {
                  setModalVisible(false);
                  bottomSheetRef.current?.dismiss();
                  navigation.navigate('NewLocation');
                }}
                style={styles.addNewLocationButton}>
                <Text style={styles.addNewLocationButtonText}>
                  + Add New Business
                </Text>
              </TouchableOpacity>
            </View>
          ) : null
        }>
        {/* All Locations option - only show on eligible routes */}
        {showAllLocationsOption && (
          <TouchableOpacity
            style={styles.locationItem}
            onPress={handleSelectAllLocations}>
            <View
              style={[
                styles.locationItemContent,
                styles.allLocationsItem,
                isAllLocations && styles.currentLocationItem,
              ]}>
              <FontAwesome6
                name="layer-group"
                iconStyle="solid"
                size={iconSize(20)}
                color={isAllLocations ? theme.colors.successDark : '#6366f1'}
                style={styles.locationIcon}
              />
              <View style={styles.locationInfo}>
                <Text style={styles.locationName}>All Locations</Text>
                <Text style={styles.locationDetails}>
                  Tap to view business-wide data
                </Text>
              </View>
              {isAllLocations && (
                <FontAwesome6
                  name="circle-check"
                  iconStyle="solid"
                  size={iconSize(20)}
                  color="#22c55e"
                  style={styles.currentLocationIcon}
                />
              )}
            </View>
          </TouchableOpacity>
        )}

        {/* Divider */}
        {showAllLocationsOption && <View style={styles.divider} />}

        {/* Individual locations */}
        <FlatList
          data={locations}
          renderItem={renderLocationItem}
          keyExtractor={item => item._id ?? ''}
          style={styles.locationsList}
          showsVerticalScrollIndicator={false}
        />
      </LocationBottomSheet>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginLeft: s(16),
  },
  selector: {
    flexDirection: 'row',
    alignItems: 'center',
    // paddingHorizontal: s(12),
    paddingVertical: s(6),
    gap: s(8),
  },
  selectorText: {
    fontSize: fs(Platform.OS === 'web' ? 14 : 16),
    lineHeight: fs(20),
    // fontFamily: GTFonts.semibold,
  },
  allLocationsIcon: {
    marginRight: s(-4),
  },
  locationItem: {
    marginBottom: v(12),
  },
  locationItemContent: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: s(15),
    borderRadius: s(8),
    borderWidth: borderWidth(2),
    borderColor: '#e9ecef',
  },
  allLocationsItem: {
    borderColor: '#e0e7ff',
    backgroundColor: '#f5f3ff',
  },
  currentLocationItem: {
    backgroundColor: theme.colors.successLight,
    borderColor: theme.colors.successLight,
  },
  locationIcon: {
    marginRight: s(12),
  },
  locationInfo: {
    flex: 1,
  },
  locationName: {
    fontSize: fs(16),
    lineHeight: fs(20),
    fontFamily: GTFonts.regular,
    fontWeight: '600',
    color: '#333',
    marginBottom: v(2),
  },
  locationDetails: {
    fontSize: fs(12),
    lineHeight: fs(16),
    fontFamily: GTFonts.regular,
    color: '#666',
  },
  badgeContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: s(5),
  },
  defaultBadge: {
    backgroundColor: '#1560bd',
    paddingHorizontal: s(8),
    paddingVertical: s(4),
    borderRadius: s(12),
  },
  defaultBadgeText: {
    fontSize: fs(10),
    fontFamily: GTFonts.regular,
    fontWeight: '600',
    color: 'white',
  },
  currentLocationIcon: {
    marginLeft: s(5),
  },
  locationItemWeb: {
    ...(Platform.OS === 'web'
      ? ({
          cursor: 'pointer',
          transition: 'background-color 0.15s ease-out',
        } as any)
      : {}),
  },
  locationItemHovered: {
    backgroundColor: 'rgba(0,0,0,0.04)',
  },
  divider: {
    height: 1,
    backgroundColor: '#e5e7eb',
    marginVertical: v(8),
    marginHorizontal: s(-12),
  },
  locationsList: {
    maxHeight: v(350),
  },
  footerContainer: {
    backgroundColor: theme.colors.surface,
    paddingHorizontal: s(8),
    paddingVertical: s(20),
    ...(Platform.OS === 'web' && {
      paddingHorizontal: s(24),
    }),
  },
  addNewLocationButton: {
    width: '100%',
    backgroundColor: '#1560bd',
    paddingVertical: s(14),
    borderRadius: s(12),
    alignItems: 'center',
  },
  addNewLocationButtonText: {
    color: 'white',
    fontFamily: PoppinsFonts.regular,
    fontWeight: '700',
    fontSize: fs(16),
  },
});

export default BackOfficeHeaderLocationPicker;
